import detectEthereumProvider from '@akkafinance/bitkeep-detect-provider'
import { AbstractConnector } from '@web3-react/abstract-connector'

export interface ProviderConnectInfo {
  chainId: string
}

export interface ProviderRpcError extends Error {
  message: string
  code: number
  data?: unknown
}

export interface AddEthereumChainParameter {
  chainId: string // Must be in hexadecimal format
  chainName?: string
  nativeCurrency?: {
    name: string
    symbol: string // 2-6 characters
    decimals: number
  }
  rpcUrls?: string[]
  blockExplorerUrls?: string[]
  iconUrls?: string[]
}

interface BitKeepEthereumProvider {
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>
  on: (event: string, listener: (...args: any[]) => void) => void
  removeListener: (event: string, listener: (...args: any[]) => void) => void
  isBitGetWallet?: boolean
  providers?: BitGetWalletProvider[]
}

type BitGetWalletProvider = {
  isBitGetWallet?: boolean
  isConnected?: () => boolean
  providers?: BitGetWalletProvider[]
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>
  on: (event: string, listener: (...args: any[]) => void) => void
  removeListener: (event: string, listener: (...args: any[]) => void) => void
}
export interface ConnectorUpdate<T = string | number> {
  provider?: any
  chainId?: T
  account?: null | string
}
/**
 * Error thrown when BitGet Wallet is not detected.
 */
export class NoBitGetWalletError extends Error {
  public constructor() {
    super('BitGetWallet not installed')
    this.name = NoBitGetWalletError.name
    Object.setPrototypeOf(this, NoBitGetWalletError.prototype)
  }
}

/**
 * Parses a chainId string from hexadecimal to a number.
 */
function parseChainId(chainId: string): number {
  return Number.parseInt(chainId, 16)
}

/**
 * Constructor arguments for BitGetWallet.
 */
export interface BitGetWalletConstructorArgs {
  actions: {
    startActivation: () => void
    update: (state: { accounts?: string[]; chainId?: number }) => void
    resetState: () => void
  }
  options?: Parameters<typeof detectEthereumProvider>[0]
  onError?: (error: Error) => void
}

/**
 * BitGetWallet Connector for web3-react v6.
 */
export class BitGetWalletConnector extends AbstractConnector {
  private provider?: BitGetWalletProvider
  private readonly options?: Parameters<typeof detectEthereumProvider>[0]
  private eagerConnection?: Promise<void>
  private readonly actions: BitGetWalletConstructorArgs['actions']
  private readonly onError?: BitGetWalletConstructorArgs['onError']

  constructor({ actions, options, onError }: BitGetWalletConstructorArgs) {
    super()
    this.options = options

    // Store actions and onError as class members if you need them
    this.actions = actions
    this.onError = onError
  }

  private async initialize(): Promise<void> {
    if (this.eagerConnection) return this.eagerConnection

    this.eagerConnection = import('@akkafinance/bitkeep-detect-provider')
      .then(async (m) => {
        const detectedProvider = (await m.default(this.options)) as BitKeepEthereumProvider

        /* if (!detectedProvider) throw new NoBitGetWalletError() */

        // Ensure the detected provider implements the `request` method
        if (typeof detectedProvider.request !== 'function') {
          throw new Error('Detected provider does not implement the required `request` method.')
        }

        this.provider = detectedProvider as BitKeepEthereumProvider

        if (this.provider.providers?.length) {
          this.provider = this.provider.providers.find((p) => p.isBitGetWallet) ?? this.provider.providers[0]
        }

        this.provider.on('connect', ({ chainId }: ProviderConnectInfo) => {
          this.actions.update({ chainId: parseChainId(chainId) })
        })

        this.provider.on('disconnect', (error: ProviderRpcError) => {
          this.actions.resetState()
          this.onError?.(error)
        })

        this.provider.on('chainChanged', (chainId: string) => {
          this.actions.update({ chainId: parseInt(chainId, 16) })
          window.location.reload()
        })

        this.provider.on('accountsChanged', (accounts: string[]) => {
          if (accounts.length === 0) {
            this.actions.resetState()
          } else {
            this.actions.update({ accounts })
          }
        })
      })
      .catch((error) => {
        this.onError?.(error)
        throw error
      })

    return this.eagerConnection
  }

  public async connectEagerly(): Promise<void> {
    /*     const cancelActivation = this.actions.startActivation()
     */
    await this.initialize()

    if (!this.provider) {
      this.actions.resetState()
      return
    }

    try {
      const [chainId, accounts] = await Promise.all([
        this.provider.request({ method: 'eth_chainId' }) as Promise<string>,
        this.provider.request({ method: 'eth_accounts' }) as Promise<string[]>,
      ])

      if (accounts.length === 0) throw new Error('No accounts returned')

      this.actions.update({
        chainId: parseChainId(chainId),
        accounts,
      })
    } catch (error) {
      this.actions.resetState()
      console.debug('Could not connect eagerly:', error)
    }
  }

  public async activate(
    desiredChainIdOrChainParameters?: number | AddEthereumChainParameter
  ): Promise<ConnectorUpdate<string | number>> {
    /* const cancelActivation = this.actions.startActivation() */

    await this.initialize()

    if (!this.provider) {
      this.actions.resetState()
      throw new NoBitGetWalletError()
    }

    try {
      const [chainId, accounts] = await Promise.all([
        this.provider.request({ method: 'eth_chainId' }) as Promise<string>,
        this.provider.request({ method: 'eth_requestAccounts' }) as Promise<string[]>,
      ])

      const receivedChainId = parseChainId(chainId)

      if (typeof desiredChainIdOrChainParameters === 'number' && desiredChainIdOrChainParameters !== receivedChainId) {
        const desiredChainIdHex = `0x${desiredChainIdOrChainParameters.toString(16)}`
        await this.provider.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: desiredChainIdHex }],
        })
      }

      this.actions.update({ chainId: receivedChainId, accounts })

      // Return the ConnectorUpdate object
      return {
        provider: this.provider,
        chainId: receivedChainId,
        account: accounts[0] || null,
      }
    } catch (error) {
      this.actions.resetState()
      throw error
    }
  }

  public async deactivate(): Promise<void> {
    this.provider = undefined
    this.actions.resetState()
  }

  public async getProvider(): Promise<any> {
    return this.provider
  }

  public async getChainId(): Promise<number> {
    if (!this.provider) {
      return 0 // Default to 0 if no provider is available
    }

    // Cast the result of `provider.request` to a string
    const chainId = (await this.provider.request({ method: 'eth_chainId' })) as string

    // Parse the chainId from hexadecimal to a number
    return parseChainId(chainId)
  }
  private ensureProvider(): BitGetWalletProvider {
    if (!this.provider) {
      throw new Error('Provider is not initialized')
    }
    return this.provider
  }
  public async getAccount(): Promise<string | null> {
    const provider = this.ensureProvider()
    const accounts = (await provider.request({ method: 'eth_accounts' })) as string[]
    return accounts.length > 0 ? accounts[0] : null
  }
}
